import { useQuery } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { api } from '../lib/api';

function fmtUsd(v: number): string {
  return `$${v.toFixed(2)}`;
}

interface TooltipPayload {
  payload: { day: string; total_usd: number; over_cap: boolean };
}
interface TooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
}

function CustomTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0]!.payload;
  return (
    <div className="card-neumorphic !p-3 !shadow-neumorphic-md">
      <p className="font-mono text-xs text-parchment-700">{d.day}</p>
      <p className="font-mono text-sm font-semibold text-forest-700">
        {fmtUsd(d.total_usd)}
      </p>
      {d.over_cap && (
        <p className="text-xs text-red-700 mt-1">over daily cap</p>
      )}
    </div>
  );
}

export function CostsPage() {
  const summaryQuery = useQuery({
    queryKey: ['cost-summary'],
    queryFn: api.costSummary,
    refetchInterval: 30_000,
  });

  const data = summaryQuery.data;

  return (
    <div>
      <h2 className="text-3xl font-bold mb-2">Costs</h2>
      <p className="text-parchment-700 mb-8">
        Per-video and per-step spend. 30-day trend + anomaly flags when a video
        exceeds the $50 per-video cap.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="card-neumorphic">
          <p className="text-xs font-mono text-parchment-700 uppercase tracking-wide">
            target / video
          </p>
          <p className="text-3xl font-bold text-forest-700 mt-2">$40</p>
        </div>
        <div className="card-neumorphic">
          <p className="text-xs font-mono text-parchment-700 uppercase tracking-wide">
            cap / video
          </p>
          <p className="text-3xl font-bold text-forest-700 mt-2">$50</p>
        </div>
        <div className="card-neumorphic">
          <p className="text-xs font-mono text-parchment-700 uppercase tracking-wide">
            daily cap
          </p>
          <p className="text-3xl font-bold text-forest-700 mt-2">$100</p>
        </div>
        <div className="card-neumorphic">
          <p className="text-xs font-mono text-parchment-700 uppercase tracking-wide">
            last 30 days
          </p>
          <p className="text-3xl font-bold text-forest-700 mt-2">
            {data ? fmtUsd(data.totals.last_30d_usd) : '—'}
          </p>
        </div>
      </div>

      {summaryQuery.isError && (
        <p className="text-sm font-mono text-red-700 mb-4">cost data unreachable</p>
      )}

      {data && (
        <>
          <div className="card-neumorphic mb-8">
            <h3 className="font-heading font-semibold mb-4">
              Daily Spend — last {data.window_days} days
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={data.daily}>
                <defs>
                  <linearGradient id="costFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4a7c59" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#4a7c59" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#d6cfc1" strokeDasharray="3 3" />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 10, fontFamily: 'monospace' }}
                  tickFormatter={(d: string) => d.slice(5)}
                />
                <YAxis
                  tick={{ fontSize: 10, fontFamily: 'monospace' }}
                  tickFormatter={(v: number) => `$${v}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine
                  y={data.totals.daily_cap_usd}
                  stroke="#dc2626"
                  strokeDasharray="3 3"
                  label={{ value: 'daily cap', position: 'right', fontSize: 10 }}
                />
                <Area
                  type="monotone"
                  dataKey="total_usd"
                  stroke="#4a7c59"
                  fill="url(#costFill)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card-neumorphic">
              <h3 className="font-heading font-semibold mb-3">By Step</h3>
              {data.steps.length === 0 ? (
                <p className="text-sm font-mono text-parchment-700">no spend yet</p>
              ) : (
                <ul className="space-y-2">
                  {data.steps.map((s) => (
                    <li
                      key={s.step_name}
                      className="flex justify-between items-center py-1 border-b border-parchment-200 last:border-0"
                    >
                      <span className="text-sm font-mono">{s.step_name}</span>
                      <span className="text-sm font-mono text-forest-700">
                        {fmtUsd(s.total_usd)} <span className="text-parchment-700 text-xs">({s.rows})</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="card-neumorphic">
              <h3 className="font-heading font-semibold mb-3">Anomalies</h3>
              {data.anomalies.length === 0 ? (
                <p className="text-sm font-mono text-parchment-700">
                  no per-video cap violations
                </p>
              ) : (
                <ul className="space-y-2">
                  {data.anomalies.map((a) => (
                    <li
                      key={a.video_id}
                      className="flex justify-between items-center py-1 border-b border-red-200 last:border-0"
                    >
                      <span className="text-xs font-mono">{a.video_id.slice(0, 16)}…</span>
                      <span className="text-sm font-mono text-red-700">
                        {fmtUsd(a.total_usd)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
